import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { useT } from "../i18n";
import { X, Copy, Check, Clock, ShareNetwork, DownloadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareModal({ item, onClose }) {
    const { t } = useT();
    const HOURS = [
        { v: 1, l: t("share_d_1h") },
        { v: 6, l: t("share_d_6h") },
        { v: 24, l: t("share_d_24h") },
        { v: 72, l: t("share_d_3d") },
        { v: 168, l: t("share_d_7d") },
    ];
    const [hours, setHours] = useState(24);
    const [busy, setBusy] = useState(false);
    const [share, setShare] = useState(null);
    const [existing, setExisting] = useState([]);
    const [copied, setCopied] = useState(false);

    const loadExisting = async () => {
        try {
            const { data } = await api.get(`/media/${item.id}/shares`);
            setExisting(data);
        } catch {}
    };

    useEffect(() => {
        loadExisting();
    }, [item.id]);

    const create = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/media/${item.id}/share`, { hours });
            setShare(data);
            loadExisting();
            toast.success(t("share_created"));
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Error");
        } finally {
            setBusy(false);
        }
    };

    const copy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success(t("share_copied"));
        } catch {
            toast.error(t("share_copy_fail"));
        }
    };

    const revoke = async (token) => {
        if (!window.confirm(t("share_revoke_confirm"))) return;
        try {
            await api.delete(`/share/${token}`);
            if (share?.token === token) setShare(null);
            loadExisting();
            toast.success(t("share_revoked"));
        } catch (e) {
            toast.error(fmtErr(e.response?.data?.detail) || "Error");
        }
    };

    const downloadQR = () => {
        if (!share) return;
        const a = document.createElement("a");
        a.href = `data:image/png;base64,${share.qr_png_base64}`;
        a.download = `share-${share.token.slice(0, 8)}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const fmtLeft = (iso) => {
        const ms = new Date(iso).getTime() - Date.now();
        if (ms <= 0) return t("share_expired");
        const h = Math.floor(ms / 3600_000);
        const m = Math.floor((ms % 3600_000) / 60_000);
        if (h >= 24) return `${Math.floor(h / 24)}z ${h % 24}h`;
        if (h >= 1) return `${h}h ${m}m`;
        return `${m}m`;
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center sm:p-6" style={{ background: "rgba(0,0,0,0.92)" }} onClick={onClose}>
            <div
                className="relative bg-[var(--surface)] border w-full max-w-lg max-h-full overflow-y-auto"
                style={{ borderColor: "var(--border)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="share-modal"
            >
                <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 border-b" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                    <div>
                        <div className="label-mono">share</div>
                        <h2 className="heading-tight text-xl flex items-center gap-2">
                            <ShareNetwork size={18} /> {t("share_create")}
                        </h2>
                    </div>
                    <button onClick={onClose} data-testid="btn-close-share" className="btn-press w-9 h-9 flex items-center justify-center border" style={{ borderColor: "var(--border)" }}>
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-5">
                    <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{item.original_name}</div>

                    {!share && (
                        <div className="space-y-3">
                            <label className="label-mono block">{t("share_duration")}</label>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                {HOURS.map((h) => (
                                    <button
                                        key={h.v}
                                        onClick={() => setHours(h.v)}
                                        data-testid={`share-h-${h.v}`}
                                        className="btn-press h-10 text-xs border"
                                        style={{
                                            borderColor: hours === h.v ? "var(--text)" : "var(--border)",
                                            background: hours === h.v ? "var(--surface-2)" : "transparent",
                                            color: "var(--text)",
                                        }}
                                    >
                                        {h.l}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={create}
                                disabled={busy}
                                data-testid="btn-create-share"
                                className="btn-press w-full h-11 flex items-center justify-center gap-2 disabled:opacity-50"
                                style={{ background: "var(--text)", color: "var(--bg)" }}
                            >
                                <ShareNetwork size={18} />
                                <span className="text-sm font-medium">{busy ? t("share_btn_busy") : t("share_btn_generate")}</span>
                            </button>
                        </div>
                    )}

                    {share && (
                        <div className="space-y-4" data-testid="share-result">
                            <div className="w-44 h-44 mx-auto p-2" style={{ background: "#fff" }}>
                                <img src={`data:image/png;base64,${share.qr_png_base64}`} alt="QR" className="w-full h-full" />
                            </div>
                            <div className="flex items-center border" style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}>
                                <input
                                    readOnly
                                    value={share.url}
                                    data-testid="share-url"
                                    className="flex-1 h-11 px-3 bg-transparent outline-none text-xs font-mono truncate"
                                    style={{ color: "var(--text)" }}
                                    onFocus={(e) => e.target.select()}
                                />
                                <button
                                    onClick={() => copy(share.url)}
                                    data-testid="btn-copy-share"
                                    className="btn-press h-11 px-3 border-l flex items-center gap-1"
                                    style={{ borderColor: "var(--border-strong)" }}
                                >
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={downloadQR} className="btn-press flex-1 h-10 flex items-center justify-center gap-2 border text-sm" style={{ borderColor: "var(--border-strong)" }} data-testid="btn-download-qr">
                                    <DownloadSimple size={14} /> {t("share_btn_download_qr")}
                                </button>
                                <button onClick={() => setShare(null)} className="btn-press flex-1 h-10 border text-sm" style={{ borderColor: "var(--border)" }}>
                                    {t("share_btn_more")}
                                </button>
                            </div>
                            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                                <Clock size={12} /> {t("share_expires_in")} {fmtLeft(share.expires_at)}
                            </div>
                        </div>
                    )}

                    {existing.length > 0 && (
                        <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
                            <div className="label-mono mb-2">{t("share_active_list")}</div>
                            <div className="space-y-2">
                                {existing.map((s) => (
                                    <div key={s.token} className="flex items-center justify-between gap-2 border p-2 text-xs" style={{ borderColor: "var(--border)" }} data-testid={`share-row-${s.token}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-mono truncate">/s/{s.token.slice(0, 12)}…</div>
                                            <div className="label-mono" style={{ color: s.active ? "var(--success)" : "var(--text-dim)" }}>
                                                {s.active ? `${t("share_active")} · ${fmtLeft(s.expires_at)}` : t("share_expired")}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => revoke(s.token)}
                                            className="btn-press w-8 h-8 flex items-center justify-center"
                                            style={{ background: "var(--danger)", color: "#fff" }}
                                            data-testid={`btn-revoke-${s.token}`}
                                        >
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
